/**
 * Commray custom domain URLs (prod / test / local).
 * Override at build time with REACT_APP_* env vars on Cloud Run.
 */

const URLS = {
  prod: {
    marketing: "https://commray.com",
    product: "https://app.commray.com",
    api: "https://api.commray.com",
    billing: "https://billing.commray.com",
  },
  test: {
    marketing: "https://test.commray.com",
    product: "https://app.test.commray.com",
    api: "https://api.test.commray.com",
    billing: "https://billing.test.commray.com",
  },
  local: {
    marketing: "http://localhost:3000",
    product: "http://localhost:3000",
    api: "http://localhost:8080",
    billing: "http://localhost:8080",
  },
};

function trimSlash(value) {
  if (value == null || String(value).trim() === "") return "";
  return String(value).replace(/\/$/, "");
}

/** Strip Cloud Run internal port from public commray.com URLs (never expose :8080 in browser redirects). */
function normalizePublicUrl(value) {
  const trimmed = trimSlash(value);
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    const isCommrayHost = /(^|\.)commray\.com$/i.test(url.hostname);
    const isLocal = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    if (isCommrayHost && !isLocal && url.port === "8080") {
      url.port = "";
      return trimSlash(url.toString());
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

function readRuntimeEnv(key) {
  if (typeof window !== "undefined" && window.__COMMRAY_RUNTIME__ && window.__COMMRAY_RUNTIME__[key]) {
    return normalizePublicUrl(window.__COMMRAY_RUNTIME__[key]);
  }
  return normalizePublicUrl(process.env[key]);
}

/** @returns {"prod"|"test"|"local"} */
export function detectCommrayEnvironment() {
  if (typeof window === "undefined") return "prod";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host === "test.commray.com" || host.endsWith(".test.commray.com")) return "test";
  return "prod";
}

export function getCommrayUrls() {
  const tier = detectCommrayEnvironment();
  const defaults = URLS[tier];
  return {
    marketing: readRuntimeEnv("REACT_APP_MARKETING_URL") || defaults.marketing,
    product: readRuntimeEnv("REACT_APP_PRODUCT_URL") || defaults.product,
    api: readRuntimeEnv("REACT_APP_BACKEND_URL") || readRuntimeEnv("API_BASE_URL") || defaults.api,
    billing: readRuntimeEnv("REACT_APP_BILLING_URL") || defaults.billing,
  };
}

export function getBackendBaseUrl() {
  const urls = getCommrayUrls();
  if (typeof window !== "undefined" && window.location.port === "3000" && detectCommrayEnvironment() === "local") {
    return "";
  }
  return urls.api;
}

export function getBillingApiBaseUrl() {
  return getCommrayUrls().billing;
}

export function getProductLoginUrl() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "/login";
    }
  }
  const explicit = normalizePublicUrl(process.env.REACT_APP_PRODUCT_LOGIN_URL);
  if (explicit) {
    return explicit.includes("/login") ? explicit : `${explicit}/login`;
  }
  return `${getCommrayUrls().product}/login`;
}

/**
 * In-app auth guard redirect. Always a router-relative path on the product app
 * (never an absolute marketing URL — React Router would append it to the current origin).
 */
export function getProductLoginRedirect() {
  return "/login";
}

/** Marketing site login page (commray.com / test.commray.com). */
export function getMarketingLoginUrl() {
  const explicit = normalizePublicUrl(process.env.REACT_APP_MARKETING_LOGIN_URL);
  if (explicit) {
    return explicit.includes("/login") ? explicit : `${explicit}/login`;
  }
  return `${getCommrayUrls().marketing}/login`;
}

export function getProductAppUrl(path = "") {
  const base = getCommrayUrls().product;
  if (!path) return base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

export function getBillingSuccessUrl() {
  return getProductAppUrl("/billing/success");
}

export function getBillingCancelUrl() {
  return getProductAppUrl("/billing/cancel");
}

/** Absolute or root-relative path for axios against the main product API. */
export function backendUrl(path) {
  const base = getBackendBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (base === "") {
    return p;
  }
  return `${base}${p}`;
}

/** Absolute URL for the Stripe / billing communications API. */
export function billingApiUrl(path) {
  const base = getBillingApiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}
